import {AfterViewInit, Component, ElementRef, OnInit, ViewChild} from '@angular/core';
import {MatTableDataSource} from '@angular/material/table';
import {Field, FieldType, MemberAddeditComponent, MemberAddeditDialogModel} from '../member-addedit/member-addedit.component';
import {VendorGroupsProductsService} from '../../../services/vendor-groups-products/vendor-groups-products.service';
import {MatDialog} from '@angular/material/dialog';
import {MatSnackBar} from '@angular/material/snack-bar';
import {UiAlertService} from '../../../services/ui-alert/ui-alert.service';
import {VendorGroupsRatesService} from '../../../services/vendor-groups-rates/vendor-groups-rates.service';
import {TeamCompaniesService} from '../../../services/teamcompanies/teamcompanies.service';
import {MatSort} from '@angular/material/sort';
import {fromEvent} from 'rxjs';
import {debounceTime, distinctUntilChanged, filter, tap} from 'rxjs/operators';
import {ActivatedRoute, Router} from '@angular/router';
import _ from 'lodash';

@Component({
  selector: 'app-vendor-groups-rates',
  templateUrl: './vendor-groups-rates.component.html',
  styleUrls: ['./vendor-groups-rates.component.scss']
})
export class VendorGroupsRatesComponent implements OnInit, AfterViewInit {
  private readonly supportedTargetTypes = new Set(['agency', 'employer']);
  private readonly targetTypeLabels: Record<string, string> = {
    agency: 'agency -> descendants',
    employer: 'company',
  };

  @ViewChild(MatSort) sort!: MatSort;
  @ViewChild('search') search: ElementRef | undefined;

  protected allColumnNames = ['createdAt', 'vendorProductId', 'label', 'companyId', 'tiers', 'metadata', 'actionmenu'];
  protected items = new MatTableDataSource<any>();
  private companies: any;
  private products: any;

  private addEditFields: Field[] = [
    {name: 'vendorProductId', label: 'Vendor Product', fieldType: FieldType.F_DROPDOWN, values: Array<any>(), required: true},
    {name: 'isGlobal', label: 'Global rate', fieldType: FieldType.F_TOGGLE},
    {
      name: 'companyIds',
      label: 'Targets',
      fieldType: FieldType.F_DROPDOWN,
      values: [],
      required: true,
      multiple: true,
      invisible: (form: any) => form.get('isGlobal')?.value === true,
    },
    {name: 'label', label: 'Label', fieldType: FieldType.F_STRING, hint: 'Optional notes to help identify this rate in the list and search.'},
    {name: 'metadata', label: 'Configuration JSON', fieldType: FieldType.F_JSON, required: true},
  ];

  constructor(
      private vendorGroupsProductsService: VendorGroupsProductsService,
      private dialog: MatDialog,
      private snackBar: MatSnackBar,
      private uiAlertService: UiAlertService,
      private vendorGroupsRatesService: VendorGroupsRatesService,
      private teamCompaniesService: TeamCompaniesService,
      private activatedRoute: ActivatedRoute,
      private router: Router
  ) {

  }

  ngOnInit(): void {
    this.reloadItems();
  }

  ngAfterViewInit(): void {
    this.activatedRoute.queryParamMap.subscribe(params => {
      // @ts-ignore
      this.search.nativeElement.value = params.get('search') || '';
      this.reloadItems();
    });
    fromEvent(this.search?.nativeElement, 'keyup').pipe(
        filter(Boolean),
        debounceTime(300),
        distinctUntilChanged(),
        tap((): void => {
          this.reloadItems();
          this.router.navigate([] , {
            relativeTo: this.activatedRoute,
            queryParams: {search: this.search?.nativeElement.value ?? ''},
            queryParamsHandling: 'merge'
          });
        })).subscribe();
  }

  private reloadItems(): void {
    this.vendorGroupsRatesService.getAllVendorGroupRates(10000, 0, this.search?.nativeElement.value ?? '').then(rates => {
      this.items.data = rates;
      this.items.sortingDataAccessor = (item: any, property: string): string | number => {
        switch (property) {
          case 'createdAt':
            return new Date(item?.createdAt || 0).getTime();
          case 'vendorProductId':
            return this.getVendorProductName(item).toLowerCase();
          case 'label':
            return this.getRateLabel(item).toLowerCase();
          case 'companyId':
            return this.getCompanyName(item).toLowerCase();
          case 'tiers':
            return this.getTiers(item).toLowerCase();
          case 'metadata':
            return JSON.stringify(item?.metadata || '').toLowerCase();
          default:
            return String(item?.[property] ?? '').toLowerCase();
        }
      };
      this.items.sort = this.sort;
    });
    this.vendorGroupsProductsService.getAllVendorGroupProducts().then(products => {
      this.products = products;
      this.addEditFields[0].values = _.sortBy(products.map((vendor: { metadata: any; vendorProductName: any; id: any; }) => `${vendor.vendorProductName} (${vendor.metadata.shortName} ${vendor.metadata.tag || ''}):${vendor.id}`));
    });
    this.teamCompaniesService.getCompanies().then(companies => {
      this.companies = companies;
      const targetsField = this.addEditFields.find(field => field.name === 'companyIds');
      if (targetsField) {
        targetsField.values = _.sortBy(
          (companies || [])
            .filter((company: { type: string; }) => this.supportedTargetTypes.has(company.type))
            .map((company: any) => `${this.getTargetLabel(company)}:${company.id}`)
        );
      }
    });
  }

  getTiers(item: any): string {
    return((item.metadata.tiers || item.metadata.compact.tiers).map((tier: { tier: any; }) => tier.tier).join(', '));
  }

  getCompanyName(item: any): string {
    if (this.isGlobalRate(item)) {
      return 'Global';
    }

    const names = (item.companyIds || [])
        .map((id: string) => {
          const company = this.companies?.find((c: any) => c.id === id);
          return company ? this.getTargetLabel(company) : id;
        })
        .filter((name: string | null) => name) // remove nulls
        .join(', ');
    return names;
  }

  private getTargetLabel(company: any): string {
    if (!company?.name) {
      return '';
    }
    const typeLabel = this.targetTypeLabels[company.type] || company.type;
    return typeLabel ? `${company.name} (${typeLabel})` : company.name;
  }

  getVendorProductName(item: any): string {
   const product = this.products?.find((vendorProduct: { id: any; }) => vendorProduct.id === item.vendorProductId);
   return product ? `${product.vendorProductName} ${product.metadata.name} (${product.metadata.shortName}${product.metadata.tag ? ' ' + product.metadata.tag : ''})` : '';
  }

  getRateLabel(item: any): string {
    return item?.metadata?.label || '';
  }

  isGlobalRate(item: any): boolean {
    return item?.metadata?.targetScope === 'global';
  }

  addItem(): void {
    const dialogRef = this.dialog.open(MemberAddeditComponent, {
      data: new MemberAddeditDialogModel(null, this.addEditFields, 'Group Rate')
    });
    dialogRef.afterClosed().subscribe(dialogResult => {
      if (dialogResult) {
        const payload = this.buildRatePayload(dialogResult);
        this.vendorGroupsRatesService.createVendorGroupRate(payload.vendorProductId, payload.companyIds, payload.metadata).then(() => {
          this.reloadItems();
          this.snackBar.open('New group rate created');
        }).catch(error => this.presentSaveError(error));
      }
    });
  }

  editItem(item: any): void {
    const dialogItem = this.buildDialogItem(item);
    const dialogRef = this.dialog.open(MemberAddeditComponent, {
      data: new MemberAddeditDialogModel(dialogItem, this.addEditFields, 'Group Rate')
    });
    dialogRef.afterClosed().subscribe(dialogResult => {
      if (dialogResult) {
        this.vendorGroupsRatesService.modifyVendorGroupRate(item.id, this.buildRatePayload(dialogResult)).then(() => {
          this.reloadItems();
          this.snackBar.open('Group rate updated');
        }).catch(error => this.presentSaveError(error));
      }
    });
  }

  duplicateItem(item: any): void {
    const dialogItem = {
      ...this.buildDialogItem(item),
      companyIds: [],
      isGlobal: false,
    };
    const dialogRef = this.dialog.open(MemberAddeditComponent, {
      data: new MemberAddeditDialogModel(dialogItem, this.addEditFields, 'Group Rate', true, 'Add')
    });
    dialogRef.afterClosed().subscribe(dialogResult => {
      if (dialogResult) {
        const payload = this.buildRatePayload(dialogResult);
        this.vendorGroupsRatesService.createVendorGroupRate(
          payload.vendorProductId,
          payload.companyIds,
          payload.metadata
        ).then(() => {
          this.reloadItems();
          this.snackBar.open('Duplicate group rate created', '', { duration: 3000 });
        }).catch(error => this.presentSaveError(error));
      }
    });
  }

  removeItem(item: any): void {
    this.uiAlertService.presentAlertConfirm('Do you really want to remove this group rate?').then(ok => {
      this.vendorGroupsRatesService.deleteVendorGroupRate(item.id).then(() => {
        this.reloadItems();
        this.snackBar.open('Group rate removed');
      });
    });
  }

  truncateJSONString(str: string, maxLength: number): string {
    str = JSON.stringify(str);
    return str.length <= maxLength ? str : str.slice(0, maxLength - 3) + '...';
  }

  private presentSaveError(error: any): void {
    const message = error?.data?.error || 'Unable to save group rate';
    this.snackBar.open(message, '', {
      duration: 5000,
      panelClass: ['error-snackbar'],
    });
  }

  private buildRatePayload(dialogResult: any): any {
    const metadata = _.cloneDeep(dialogResult?.metadata) || {};
    const label = dialogResult?.label?.trim?.() || '';
    const isGlobal = dialogResult?.isGlobal === true;

    if (label) {
      metadata.label = label;
    } else {
      delete metadata.label;
    }

    if (isGlobal) {
      metadata.targetScope = 'global';
    } else {
      delete metadata.targetScope;
    }

    return {
      vendorProductId: dialogResult?.vendorProductId,
      companyIds: isGlobal ? [] : dialogResult?.companyIds,
      metadata,
    };
  }

  private buildDialogItem(item: any): any {
    const metadata = _.cloneDeep(item?.metadata) || {};
    delete metadata.label;
    delete metadata.targetScope;

    return {
      ...item,
      label: this.getRateLabel(item),
      isGlobal: this.isGlobalRate(item),
      metadata,
    };
  }

}
